import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { businessTypes, businessAssetFields } from '../config/businessZakatConfig';
import { countries, languages, nisabConstants } from '../config/zakatConfig';
import { computeBusinessZakat, computeLivestockZakat, computeZakatFitr } from '../utils/businessCalc';
import { secureStorage } from '../utils/secureStorage';
import { detectCountryCode } from '../utils/detectCountry';

const BusinessZakatContext = createContext(null);

// API URLs
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const CURRENCY_API_URL = 'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/usd.json';

export const BusinessZakatProvider = ({ children }) => {
  // Core state
  const [selectedCountry, setSelectedCountry] = useState(countries[0]);
  const [selectedLanguage, setSelectedLanguage] = useState(languages[0]);
  const [selectedBusinessType, setSelectedBusinessType] = useState(null);
  const [nisabStandard, setNisabStandard] = useState('gold'); // 'gold' or 'silver'
  const [showDualNisab, setShowDualNisab] = useState(false);
  
  // Manual rate inputs
  const [manualRates, setManualRates] = useState({
    gold: 0,
    silver: 0,
    bitcoin: 0,
    ethereum: 0,
    customAssets: {}
  });
  const [useManualRates, setUseManualRates] = useState(false);
  
  // Live prices
  const [liveGoldPrice, setLiveGoldPrice] = useState(0);
  const [liveSilverPrice, setLiveSilverPrice] = useState(0);
  const [exchangeRates, setExchangeRates] = useState({});
  const [pricesLoading, setPricesLoading] = useState(true);
  const [lastPriceUpdate, setLastPriceUpdate] = useState(null);
  
  // Asset values for current business
  const [assetValues, setAssetValues] = useState({});
  const [deductionValues, setDeductionValues] = useState({});
  
  // Calculation results
  const [calculationResult, setCalculationResult] = useState(null);
  const [savedCalculations, setSavedCalculations] = useState([]);
  
  // Instalment plan
  const [instalmentMonths, setInstalmentMonths] = useState(12);
  
  // Zakat al-Fitr
  const [fitrFamilyMembers, setFitrFamilyMembers] = useState(1);

  // Fetch exchange rates
  const fetchExchangeRates = useCallback(async () => {
    try {
      const response = await fetch(CURRENCY_API_URL);
      const data = await response.json();
      if (data && data.usd) {
        setExchangeRates(data.usd);
        
        // Fetch live gold/silver from backend
        let goldPerGramUSD = 163; // fallback
        let silverPerGramUSD = 2.0; // fallback
        try {
          const metalResp = await fetch(`${BACKEND_URL}/api/metal-prices`);
          if (metalResp.ok) {
            const metalData = await metalResp.json();
            goldPerGramUSD = metalData.gold_usd_per_gram;
            silverPerGramUSD = metalData.silver_usd_per_gram;
          }
        } catch (e) {
          // Expected offline / rate-limit fallback — log at info level so
          // the CRA dev overlay does not flag this as an uncaught error.
          console.info('Metal prices fallback used');
        }
        
        // Get exchange rate for selected currency
        const currencyCode = selectedCountry.currency.toLowerCase();
        const rate = data.usd[currencyCode] || 1;
        
        setLiveGoldPrice(goldPerGramUSD * rate);
        setLiveSilverPrice(silverPerGramUSD * rate);
        setLastPriceUpdate(new Date());
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch exchange rates:', error);
      // Use fallback prices
      setLiveGoldPrice(selectedCountry.currency === 'USD' ? 163 : 13600);
      setLiveSilverPrice(selectedCountry.currency === 'USD' ? 2 : 166);
    } finally {
      setPricesLoading(false);
    }
  }, [selectedCountry]);

  // Auto-detect country on mount (via backend which reads real client IP from proxy headers)
  useEffect(() => {
    const detectCountry = async () => {
      try {
        const code = await detectCountryCode();
        const match = countries.find(c => c.code === code);
        if (match) setSelectedCountry(match);
      } catch (e) {
        // Expected fallback (offline, CORS, IP-geo blocked). Info-level
        // so the CRA dev overlay does not flag this as an uncaught error.
        console.info('Country auto-detect fallback:', e?.message || e);
      }
      // Always keep English as default language
      const enLang = languages.find(l => l.code === 'en');
      if (enLang) setSelectedLanguage(enLang);
    };
    detectCountry();
    // Mount-only: country auto-detect should not re-run when state setters change.
  }, []);

  // Fetch prices on mount and every hour
  useEffect(() => {
    fetchExchangeRates();
    const interval = setInterval(fetchExchangeRates, 3600000); // Every hour
    return () => clearInterval(interval);
  }, [fetchExchangeRates]);

  // Get effective gold/silver price (manual or live)
  const getGoldPrice = useCallback(() => {
    return useManualRates && manualRates.gold > 0 ? manualRates.gold : liveGoldPrice;
  }, [useManualRates, manualRates.gold, liveGoldPrice]);

  const getSilverPrice = useCallback(() => {
    return useManualRates && manualRates.silver > 0 ? manualRates.silver : liveSilverPrice;
  }, [useManualRates, manualRates.silver, liveSilverPrice]);

  // Update manual rate
  const updateManualRate = useCallback((asset, value) => {
    setManualRates(prev => ({
      ...prev,
      [asset]: parseFloat(value) || 0
    }));
  }, []);

  // Add custom asset rate
  const addCustomAssetRate = useCallback((assetName, rate) => {
    setManualRates(prev => ({
      ...prev,
      customAssets: {
        ...prev.customAssets,
        [assetName]: parseFloat(rate) || 0
      }
    }));
  }, []);

  // Calculate nisab thresholds
  const calculateNisabGold = useCallback(() => {
    return nisabConstants.goldGrams * getGoldPrice();
  }, [getGoldPrice]);

  const calculateNisabSilver = useCallback(() => {
    return nisabConstants.silverGrams * getSilverPrice();
  }, [getSilverPrice]);

  // Calculate business zakat (delegates pure logic to utils/businessCalc)
  const calculateBusinessZakat = useCallback(() => {
    const result = computeBusinessZakat({
      businessId: selectedBusinessType,
      assetValues,
      deductionValues,
      nisabStandard,
      goldPricePerGram: getGoldPrice(),
      silverPricePerGram: getSilverPrice(),
      showDualNisab,
      instalmentMonths,
      currency: selectedCountry.currency,
      currencySymbol: selectedCountry.currencySymbol,
    });
    setCalculationResult(result);
    return result;
  }, [
    selectedBusinessType,
    assetValues,
    deductionValues,
    nisabStandard,
    showDualNisab,
    instalmentMonths,
    getGoldPrice,
    getSilverPrice,
    selectedCountry,
  ]);

  // Calculate livestock zakat (kept for backward compatibility / direct use)
  const calculateLivestockZakat = useCallback(
    () => computeLivestockZakat(assetValues),
    [assetValues]
  );

  // Calculate Zakat al-Fitr
  const calculateZakatFitr = useCallback(
    () => computeZakatFitr(selectedCountry.code, fitrFamilyMembers),
    [selectedCountry, fitrFamilyMembers]
  );

  // Save calculation
  const saveCalculation = useCallback((calculation) => {
    const saved = {
      ...calculation,
      id: Date.now(),
      savedAt: new Date()
    };
    setSavedCalculations(prev => [...prev, saved].slice(-50));
    // Also save to localStorage
    const existing = JSON.parse(secureStorage.getItem('zakatCalculations') || '[]');
    existing.push(saved);
    secureStorage.setItem('zakatCalculations', JSON.stringify(existing.slice(-50)));
    return saved;
  }, []);

  // Load saved calculations
  useEffect(() => {
    const saved = JSON.parse(secureStorage.getItem('zakatCalculations') || '[]');
    setSavedCalculations(saved);
  }, []);

  // Format currency
  const formatCurrency = useCallback((value) => {
    if (typeof value !== 'number' || isNaN(value)) return `${selectedCountry.currencySymbol}0`;
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: selectedCountry.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }, [selectedCountry]);

  // Update asset value
  const updateAssetValue = useCallback((field, value) => {
    setAssetValues(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  }, []);

  // Update deduction value
  const updateDeductionValue = useCallback((field, value) => {
    setDeductionValues(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  }, []);

  // Reset calculator
  const resetCalculator = useCallback(() => {
    setAssetValues({});
    setDeductionValues({});
    setCalculationResult(null);
  }, []);

  // Update country
  const updateCountry = useCallback((countryCode) => {
    const country = countries.find(c => c.code === countryCode);
    if (country) {
      setSelectedCountry(country);
      // Refresh prices for new currency
      fetchExchangeRates();
    }
  }, [fetchExchangeRates]);

  // Update language
  const updateLanguage = useCallback((langCode) => {
    const lang = languages.find(l => l.code === langCode);
    if (lang) setSelectedLanguage(lang);
  }, []);

  const value = {
    // State
    selectedCountry,
    selectedLanguage,
    selectedBusinessType,
    nisabStandard,
    showDualNisab,
    manualRates,
    useManualRates,
    liveGoldPrice,
    liveSilverPrice,
    exchangeRates,
    pricesLoading,
    lastPriceUpdate,
    assetValues,
    deductionValues,
    calculationResult,
    savedCalculations,
    instalmentMonths,
    fitrFamilyMembers,
    
    // Config
    businessTypes,
    businessAssetFields,
    countries,
    languages,
    nisabConstants,
    
    // Actions
    setSelectedBusinessType,
    setNisabStandard,
    setShowDualNisab,
    setUseManualRates,
    updateManualRate,
    addCustomAssetRate,
    updateAssetValue,
    updateDeductionValue,
    updateCountry,
    updateLanguage,
    setInstalmentMonths,
    setFitrFamilyMembers,
    resetCalculator,
    saveCalculation,
    fetchExchangeRates,
    
    // Calculations
    getGoldPrice,
    getSilverPrice,
    calculateNisabGold,
    calculateNisabSilver,
    calculateBusinessZakat,
    calculateLivestockZakat,
    calculateZakatFitr,
    
    // Helpers
    formatCurrency
  };

  return (
    <BusinessZakatContext.Provider value={value}>
      {children}
    </BusinessZakatContext.Provider>
  );
};

export const useBusinessZakat = () => {
  const context = useContext(BusinessZakatContext);
  if (!context) {
    throw new Error('useBusinessZakat must be used within a BusinessZakatProvider');
  }
  return context;
};

export default BusinessZakatContext;
